import { PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import { getInstructionDiscriminator, getMayhemProgram } from '../program/mayhemProgram';
import { convertDisplayTokenAmountToAtomic } from '../utils/tradeUnits';

function encodeU64(value) {
  const buffer = new Uint8Array(8);
  let remainingValue = value;

  for (let index = 0; index < 8; index += 1) {
    buffer[index] = Number(remainingValue & 255n);
    remainingValue >>= 8n;
  }

  return buffer;
}

function buildInstructionKeys(instructionDefinition, accounts) {
  return instructionDefinition.accounts.map((account) => {
    const pubkey = accounts[account.name];

    if (!pubkey) {
      throw new Error(`Missing required sell account: ${account.name}`);
    }

    return {
      pubkey,
      isSigner: account.isSigner,
      isWritable: account.isMut,
    };
  });
}

export async function sellCurrentGame({
  tokenAmount,
  globalConfig,
  globalConfigPubkey,
  currentGameState,
  currentGamePubkey,
  dumpVaultPubkey,
  sellerTokenAta,
}) {
  const program = getMayhemProgram();
  const provider = program.getPhantomProvider();
  const walletPublicKey = provider?.publicKey || null;

  if (!provider || !walletPublicKey) {
    throw new Error('Connect a Phantom wallet before selling.');
  }

  if (!globalConfig || !globalConfigPubkey) {
    throw new Error('Global config is not ready.');
  }

  if (!currentGameState || !currentGamePubkey) {
    throw new Error('Current game is not ready.');
  }

  if (!dumpVaultPubkey) {
    throw new Error('Global dump vault is not ready.');
  }

  if (!currentGameState.reserve_vault || !currentGameState.mint) {
    throw new Error('Current game accounts are incomplete.');
  }

  if (!sellerTokenAta) {
    throw new Error('Current round token account is not available.');
  }

  const tokensIn = convertDisplayTokenAmountToAtomic(tokenAmount, currentGameState.decimals);
  const instructionDefinition = program.getInstructionDefinition('sell');

  if (!instructionDefinition) {
    throw new Error('Sell instruction was not found in the local IDL.');
  }

  const accounts = {
    globalConfig: new PublicKey(globalConfigPubkey),
    gameState: new PublicKey(currentGamePubkey),
    gameReserveVault: new PublicKey(currentGameState.reserve_vault),
    globalDumpVault: new PublicKey(dumpVaultPubkey),
    platformTreasury: program.getPlatformTreasuryPda(),
    roundMint: new PublicKey(currentGameState.mint),
    sellerTokenAccount: new PublicKey(sellerTokenAta),
    seller: walletPublicKey,
    tokenProgram: program.tokenProgramId,
  };

  const discriminator = await getInstructionDiscriminator('sell');
  const data = new Uint8Array([...discriminator, ...encodeU64(tokensIn)]);
  const instruction = new TransactionInstruction({
    programId: program.programId,
    keys: buildInstructionKeys(instructionDefinition, accounts),
    data,
  });

  const { blockhash, lastValidBlockHeight } = await program.connection.getLatestBlockhash('confirmed');
  const transaction = new Transaction({
    feePayer: walletPublicKey,
    recentBlockhash: blockhash,
  }).add(instruction);

  const response = await provider.signAndSendTransaction(transaction);
  const signature = typeof response === 'string' ? response : response.signature;

  await program.connection.confirmTransaction(
    {
      signature,
      blockhash,
      lastValidBlockHeight,
    },
    'confirmed'
  );

  return signature;
}